/**
 * The chats list's rows, from OnlyFans chats (`/v1/chats/get`, skip_users=all)
 * plus the fans users/list returned. A faithful port of the CRM list
 * (crm/src/public/chats/chats.js renderChatList) without its DOM.
 */

export type Raw = Record<string, unknown>

export interface ChatRow {
  fanId: string
  name: string
  /** False while the name is only the `Fan <id>` stand-in. */
  named: boolean
  handle: string
  avatar: string | null
  /** ISO time of the last message. */
  time: string
  preview: { kind: 'text'; text: string } | { kind: 'media' } | { kind: 'empty' }
  /** The whole last message, for the hover tooltip. */
  previewFull: string
  /** Unread messages, at most 100 (shown as a number, 99+ above 99). */
  unread: number
  muted: boolean
  creator: boolean
  /** The other side is a creator account (OnlyFans isRealPerformer), shown shaded. */
  performer: boolean
  pinned: boolean
  /** What the fan spent on this account; null when OnlyFans sent no spend field. */
  spent: number | null
  newFan: boolean
  /** OnlyFans says the fan is online now (users/list). */
  online: boolean
  /** The last message is the account's, not the fan's (shown as "You: …"). */
  fromMe: boolean
}

const obj = (v: unknown): Raw => (v && typeof v === 'object' ? (v as Raw) : {})

export function chatFanId(chat: Raw): string {
  return String(obj(chat.withUser).id ?? chat.withUserId ?? chat.fan_id ?? chat.id ?? '')
}

const USER_FIELDS = ['id', 'name', 'username', 'userName', 'avatar', 'avatarThumbs']

/**
 * The users in a users/list answer. OnlyFans sends a map keyed by user id,
 * sometimes wrapped in `data`; older paths sent an array, `{list}` or `{users}`
 * (ui.js normalizeUsersListPayload).
 */
export function usersFromListAnswer(answer: unknown): Raw[] {
  const outer = obj(answer)
  const raw = outer.list ?? outer.users ?? outer.data ?? answer
  if (Array.isArray(raw)) return raw.filter((u): u is Raw => Boolean(u) && typeof u === 'object')
  if (!raw || typeof raw !== 'object') return []
  const map = raw as Raw
  if (Array.isArray(map.list) || Array.isArray(map.users)) return usersFromListAnswer(map)
  const entries = Object.entries(map)
  const sample = obj(entries[0]?.[1])
  if (!USER_FIELDS.some((f) => sample[f] != null)) return []
  return entries.flatMap(([key, u]) => {
    if (!u || typeof u !== 'object') return []
    const o = u as Raw
    const id = String(o.id ?? o.user_id ?? o.fan_id ?? key).trim()
    return id ? [{ ...o, id }] : []
  })
}

/** OnlyFans often names a fan "Fan #123" / "User #123" until users/list answers. */
export function isPlaceholderName(raw: unknown): boolean {
  const s = String(raw ?? '').trim()
  return !s || /^(fan|user)\s*#\s*\d+$/i.test(s) || /^@\s*fan_\d+$/i.test(s)
}

export function stripHtml(raw: unknown): string {
  return String(raw ?? '')
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function lastMessage(chat: Raw): Raw {
  return obj(chat.lastMessage ?? chat.last_message)
}

function isMediaOnly(m: Raw, text: string): boolean {
  if (text) return false
  if (m.giphyId || m.giphy_id) return true
  if (Array.isArray(m.media) && m.media.length > 0) return true
  if (/gif|giphy|media|photo|video|image|audio|voice|file|attachment/.test(String(m.type ?? m.messageType ?? '').toLowerCase())) return true
  return Boolean(m.hasMedia ?? m.has_media ?? m.isMedia ?? m.is_media)
}

/** An OnlyFans user's avatar, smallest good thumb first. */
export function avatarOf(u: Raw): string | null {
  const thumbs = obj(u.avatarThumbs)
  return String(thumbs.c144 || thumbs.c50 || u.avatar || '') || null
}

/** Which last message a chat shows: a read state set on screen holds until it changes. */
export function lastMessageKey(chat: Raw): string {
  const m = lastMessage(chat)
  return `${String(m.id ?? '')}|${String(m.createdAt ?? m.created_at ?? chat.updatedAt ?? '')}`
}

/** Unread messages on a chat (capped at 100, so "99+" can show); a fan's unread last message counts as 1. */
export function unreadCount(chat: Raw): number {
  const raw = Number(chat.unreadMessagesCount ?? chat.unread_messages_count ?? chat.unread_count ?? chat.unreadCount ?? chat.unread ?? 0)
  if (Number.isFinite(raw) && raw > 0) return Math.min(100, Math.floor(raw))
  if (chat.hasUnread === true || chat.has_unread === true) return 1
  const m = lastMessage(chat)
  const fromMe = m.fromMe === true || m.from_me === true || m.isFromMe === true || m.is_from_me === true
  return !fromMe && (m.isRead ?? m.is_read ?? m.read) === false ? 1 : 0
}

export function isMutedChat(chat: Raw): boolean {
  return Boolean(
    chat.muted || chat.isMuted || chat.is_muted || chat.mutedNotifications || chat.isMutedNotifications || chat.notificationsMuted,
  )
}

const CREATOR_VALUES = new Set(['creator', 'model', 'performer'])
const CREATOR_FIELDS = ['role', 'status', 'type', 'accountType', 'account_type', 'userType', 'user_type', 'profileType', 'profile_type', 'kind']

function isCreatorProfile(p: unknown): boolean {
  const o = obj(p)
  if (o.isCreator === true || o.is_creator === true || o.creator === true) return true
  return CREATOR_FIELDS.some((f) => CREATOR_VALUES.has(String(o[f] ?? '').trim().toLowerCase()))
}

/** A chat with another creator (chat-order.js): listed after every fan. */
export function isCreatorChat(chat: Raw): boolean {
  return [chat, chat.withUser, chat.user, chat.fromUser, chat.from_user].some(isCreatorProfile)
}

/**
 * A verified creator on the other side: users/list's strict `isRealPerformer`
 * (not the broad `isPerformer`, which ordinary fans can carry — brain ADR 0045),
 * or a chat already marked as a creator's.
 */
export function isPerformer(chat: Raw, user: Raw): boolean {
  return user.isRealPerformer === true || user.is_real_performer === true || isCreatorChat(chat)
}

/**
 * The CRM list's order: creator chats moved after every fan (chat-order.js),
 * then unread first and pinned next, otherwise OnlyFans' newest-first order.
 */
export function orderRows(rows: ChatRow[], rankUnread?: Map<string, number>): ChatRow[] {
  const creatorsLast = [...rows.filter((r) => !r.creator), ...rows.filter((r) => r.creator)]
  const rank = (r: ChatRow) => ((rankUnread?.get(r.fanId) ?? r.unread) > 0 ? 0 : r.pinned ? 1 : 2)
  return creatorsLast
    .map((row, i) => ({ row, i }))
    .sort((a, b) => rank(a.row) - rank(b.row) || a.i - b.i)
    .map((x) => x.row)
}

/**
 * Total the fan spent, from the first field OnlyFans filled (chats.js getChatSpentAmount).
 * The fan's spend is subscribedOnData (their subscription on this account), the
 * same field the fan panel reads; subscribedByData is the account's subscription
 * to the fan and is $0 for a paying fan.
 */
export function spentAmount(chat: Raw, user: Raw): number | null {
  const sub = obj(user.subscribedOnData ?? user.subscribed_on_data)
  const candidates = [
    sub.totalSumm, sub.total_sum, user.totalSpent, user.total_spent, user.total_spent_usd, user.spent, user.tipsTotal,
    chat.totalSpent, chat.total_spent, chat.total_spent_usd, chat.spent, chat.tipsTotal,
  ]
  let zero = false
  for (const c of candidates) {
    const n = String(c ?? '').replace(/[$,\s]/g, '')
    if (!n) continue
    const amount = Number(n)
    if (!Number.isFinite(amount) || amount < 0) continue
    if (amount > 0) return amount
    zero = true
  }
  return zero ? 0 : null
}

export function toChatRow(chat: Raw, user: Raw | undefined, opts: { pinned?: boolean; customName?: string } = {}): ChatRow {
  const fanId = chatFanId(chat)
  const withUser = { ...obj(chat.withUser), ...(user ?? {}) }
  const official = [withUser.displayName, withUser.name, chat.userName].find((n) => !isPlaceholderName(n))
  const name = opts.customName?.trim() || (official ? String(official).trim() : `Fan ${fanId}`)
  const username = String(withUser.username ?? '').trim()
  const m = lastMessage(chat)
  const full = stripHtml(m.text ?? m.message ?? chat.preview ?? '')
  const preview: ChatRow['preview'] = isMediaOnly(m, full) ? { kind: 'media' } : full ? { kind: 'text', text: full.slice(0, 50) } : { kind: 'empty' }
  return {
    fanId,
    name,
    named: Boolean(opts.customName?.trim() || official),
    handle: username ? (username.startsWith('@') ? username : `@${username}`) : '',
    avatar: avatarOf(withUser),
    time: String(m.createdAt ?? m.created_at ?? chat.updatedAt ?? chat.updated_at ?? ''),
    preview,
    previewFull: full,
    unread: unreadCount(chat),
    muted: isMutedChat(chat),
    creator: isCreatorChat(chat),
    performer: isPerformer(chat, withUser),
    pinned: opts.pinned === true,
    spent: spentAmount(chat, withUser),
    newFan: withUser.newFan === true || withUser.new_fan === true,
    online: withUser.isOnline === true || String(withUser.lastSeen ?? '').toLowerCase() === 'online',
    fromMe: Boolean(m.fromUser) && String(obj(m.fromUser).id ?? '') !== fanId,
  }
}

/** Chat list / message time: today → HH:mm, yesterday → "Yesterday", older → day + month (+ year when not this year). */
export function formatChatTime(iso: string, now: Date, locale: string, yesterday: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const diff = Math.round((day(now) - day(d)) / 86_400_000)
  if (diff <= 0) return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })
  if (diff === 1) return yesterday
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === now.getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: '2-digit' }
  return d.toLocaleDateString(locale, opts).replace(/\s*г\./, '').replace(/\./g, '').trim()
}
